/**
 * File Grid Component
 * Displays files and directories in a grid layout
 */

import React, { useMemo } from 'react';
import { StyledCheckboxContainer, StyledCheckboxInput, StyledDeleteButton, StyledFileDate, StyledFileGrid, StyledFileGridItemContainer, StyledFileIcon, StyledFileInfo, StyledFileName, StyledFileSize, StyledThumbnail, StyledThumbnailImage } from './styles';
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'ico', 'bmp'];
export function FileGrid({
  files,
  selectedFiles,
  onSelectFile,
  onDoubleClick,
  onDelete,
  allowMultiple
}) {
  const [hoveredItem, setHoveredItem] = React.useState(null);
  function getFileExtension(filename) {
    return filename.split('.').pop()?.toLowerCase() || '';
  }
  function isImageFile(filename) {
    const ext = getFileExtension(filename);
    return IMAGE_EXTENSIONS.includes(ext);
  }
  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      if (a.IsDirectory !== b.IsDirectory) {
        return a.IsDirectory ? -1 : 1;
      }
      return a.ObjectName.localeCompare(b.ObjectName);
    });
  }, [files]);
  return _jsx(StyledFileGrid, {
    children: sortedFiles.map(file => {
      const isSelected = selectedFiles.has(file.publicUrl);
      const isImage = !file.IsDirectory && isImageFile(file.ObjectName);
      const isHovered = hoveredItem === file.Key;
      return _jsxs(StyledFileGridItemContainer, {
        isSelected: isSelected,
        isHovered: isHovered,
        onDoubleClick: () => onDoubleClick(file),
        onClick: () => {
          if (!file.IsDirectory) {
            onSelectFile(file.publicUrl);
          }
        },
        onMouseEnter: () => setHoveredItem(file.Key),
        onMouseLeave: () => setHoveredItem(null),
        children: [_jsxs(StyledThumbnail, {
          children: [file.IsDirectory ? _jsx(StyledFileIcon, {
            children: "\uD83D\uDCC1"
          }) : isImage ? _jsx(StyledThumbnailImage, {
            src: file.publicUrl,
            alt: file.ObjectName,
            loading: "lazy"
          }) : _jsx(StyledFileIcon, {
            children: "\uD83D\uDCC4"
          }), !file.IsDirectory && _jsx(StyledCheckboxContainer, {
            visible: isHovered || isSelected,
            children: _jsx(StyledCheckboxInput, {
              type: allowMultiple ? 'checkbox' : 'radio',
              checked: isSelected,
              onChange: () => onSelectFile(file.publicUrl),
              onClick: e => e.stopPropagation()
            })
          }), !file.IsDirectory && _jsx(StyledDeleteButton, {
            visible: isHovered,
            onClick: e => {
              e.stopPropagation();
              onDelete(file.Key);
            },
            title: "Delete file",
            children: "\uD83D\uDDD1\uFE0F"
          })]
        }), _jsx(StyledFileName, {
          title: file.ObjectName,
          children: file.ObjectName
        }), _jsxs(StyledFileInfo, {
          children: [!file.IsDirectory && _jsx(StyledFileSize, {
            children: formatFileSize(file.Size)
          }), _jsx(StyledFileDate, {
            children: formatDate(file.LastModified)
          })]
        })]
      }, file.Key);
    })
  });
}
function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
function formatDate(dateString) {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return '';
  }
}
export default FileGrid;